import { Box, Button, Dialog, Stack, Theme, Typography, useTheme } from '@mui/material';
import { useDispatch, useSelector } from 'react-redux';
import { useHistory } from 'react-router';
import {
  cancelPlaceOrderOnSkip,
  runPlaceOrderOnSkip,
} from '../../redux-store/nomineeRetryRegistry';
import { RootStateType } from '../../redux-store/reducers';
import { CLEAR_NOMINEE_POPUP } from '../../redux-store/types/auth';


export const NOMINEE_STATUS = {
  ADD_OR_SKIP: 495,
  REQUIRED: 496,
} as const;

export default function NomineeRequiredPopup(): JSX.Element | null {
  const theme = useTheme();
  const dispatch = useDispatch();
  const history = useHistory();
  const open = useSelector((state: RootStateType) => state.nomineePopup?.open ?? false);
  const statusCode = useSelector((state: RootStateType) => state.nomineePopup?.statusCode ?? null);
  const {
    investorClientId = '',
    investorName = '',
    applicationIdForNomineeCheck=''
  } = useSelector((state: RootStateType) => state.id);
  const isMandatory = statusCode === NOMINEE_STATUS.REQUIRED;

  const handleClose = () => {
    dispatch({ type: CLEAR_NOMINEE_POPUP });
    const placeOrder = runPlaceOrderOnSkip();
    if (placeOrder) {
      Promise.resolve(placeOrder()).catch(() => {
        // swallow here; the originating screen handles its own errors
      });
    }
  };

  const handleManageNominee = () => {
    cancelPlaceOrderOnSkip();
    dispatch({ type: CLEAR_NOMINEE_POPUP });
    if (isMandatory) {
      history.push('/nominee/edit-nominee', {
        applicationId:applicationIdForNomineeCheck,
      });
      return;
    }

    history.push('/nominee/updateNominee', {
      clientCode: investorClientId,
      customerName: investorName,
    });
  };

  if (!open) return null;

  return (
    <Dialog
      open={open}
      //for 496 status code the popup wont close hard stop there need to add nominee
      onClose={isMandatory ? undefined : handleClose}
      disableEscapeKeyDown={isMandatory}
      maxWidth="md"
      sx={(theme: Theme) => ({
        '& .MuiPaper-root': {
          borderRadius: '12px',
          backgroundColor: theme.palette.common.white,
          padding: '28px 32px',
          width: { xs: '90%', sm: '560px' },
          maxWidth: '600px',
        },
      })}
    >
      <Stack spacing="14px">
        <Typography variant="h2" color={theme.palette.text.primary}>
          Add Nominee Details
        </Typography>

        <Typography variant="body_400" color={theme.palette.text.secondary}>
          As per SEBI guidelines, it is advisable to add a nominee to your Mutual Fund investments.
        </Typography>

        <Typography variant="body_400" color={theme.palette.text.secondary}>
          This will help your loved ones access your investments in case of an uncertain event.
        </Typography>

        <Typography variant="body_400" color={theme.palette.text.secondary}>
          <strong>Note:</strong> These nominee details will be used for all your future Mutual Fund transactions as well.
        </Typography>

        <Typography variant="body_400" color={theme.palette.text.secondary}>
          <strong>Note:</strong> If you don’t wish to add a nominee, you can select the Opt Out option and continue without adding one.
        </Typography>

        <Box
          sx={{
            display: 'flex',
            flexDirection: { xs: 'column', sm: 'row' },
            gap: '12px',
            mt: '6px',
          }}
        >
          {!isMandatory && (
            <Button
              variant="outlined"
              onClick={handleClose}
              sx={{
                flex: 1,
                borderRadius: '10px',
                textTransform: 'none',
                padding: '10px 16px',
                borderColor: theme.palette.primary[500],
                color: theme.palette.primary[500],
                '&:hover': {
                  borderColor: theme.palette.primary[800],
                  backgroundColor: 'transparent',
                },
              }}
            >
              <Typography variant="body_500">No, I do not wish to add</Typography>
            </Button>
          )}

          <Button
            variant="contained"
            onClick={handleManageNominee}
            sx={{
              flex: 1,
              borderRadius: '10px',
              textTransform: 'none',
              padding: '10px 16px',
              backgroundColor: theme.palette.primary[500],
              color: theme.palette.common.white,
              '&:hover': {
                backgroundColor: theme.palette.primary[800],
              },
            }}
          >
            <Typography variant="body_500" color={theme.palette.common.white}>
              {isMandatory ? 'Add Nominee' : 'Manage Nominee'}
            </Typography>
          </Button>
        </Box>
      </Stack>
    </Dialog>
  );
}
